import { line } from 'utils/components/TimeSeriesChart/line';
import {
    AnySelection,
    AnySeries,
    seriesSvgBar,
    seriesSvgMulti,
} from '@d3fc/d3fc-series';
import gridlines from 'utils/components/TimeSeriesChart/gridlines';
import { PortfolioPlWidgetConfigState } from 'widgets/portfolioPl/store/config/PortfolioPlWidgetConfigState';
import { extentLinear } from '@d3fc/d3fc-extent';
import classnames from 'classnames';

import { deriveTime } from 'utils/components/TimeSeriesChart/TimeSeriesChartData';
import { withOppositeAccessor } from 'utils/d3fc/accessors';
import { PortfolioPlLeavesTimeSeriesItem } from 'widgets/portfolioPl/chart/leaves/buckets';

const yTicks = 6;

const netSeries = line(
    (d: PortfolioPlLeavesTimeSeriesItem) => d.net,
    'net-leaves-line'
);

const buySeries = seriesSvgBar()
    .crossValue(deriveTime)
    .mainValue((d: PortfolioPlLeavesTimeSeriesItem) => d.buy)
    .bandwidth(2)
    .decorate((selection: AnySelection) => {
        selection.select('path').classed('buy-leaves-bar', true);
    });

const sellSeries = seriesSvgBar()
    .crossValue(deriveTime)
    .mainValue((d: PortfolioPlLeavesTimeSeriesItem) => d.sell)
    .bandwidth(2)
    .decorate((selection: AnySelection) => {
        selection.select('path').classed('sell-leaves-bar', true);
    });

export const getChartSeries: (
    timeSeries: PortfolioPlLeavesTimeSeriesItem[],
    chartWidth: number
) => AnySeries = () => {
    return seriesSvgMulti().series([
        gridlines(yTicks, true),
        buySeries,
        sellSeries,
        netSeries,
    ]);
};

export function computeYDomain(
    config: PortfolioPlWidgetConfigState,
    data: PortfolioPlLeavesTimeSeriesItem[]
): [number, number] {
    const { hideNet, hideSell, hideBuy } = getHiddenSeries(config);
    const { aggregationType } = config;

    const accessors = [];

    if (aggregationType === 'combined') {
        !hideNet &&
            accessors.push((d: PortfolioPlLeavesTimeSeriesItem) => d.net);
    }

    if (!hideSell) {
        accessors.push(
            ...withOppositeAccessor(
                (d: PortfolioPlLeavesTimeSeriesItem) => d.sell
            )
        );
    }

    if (!hideBuy) {
        accessors.push(
            ...withOppositeAccessor(
                (d: PortfolioPlLeavesTimeSeriesItem) => d.buy
            )
        );
    }

    return data.length > 0
        ? extentLinear().accessors(accessors).pad([0.1, 0.1])(data)
        : [0, 1];
}

export function getHideSeriesClasses(config: PortfolioPlWidgetConfigState) {
    const { hideNet, hideSell, hideBuy } = getHiddenSeries(config);

    return classnames(
        hideNet && 'hide-net',
        hideSell && 'hide-sells',
        hideBuy && 'hide-buys'
    );
}

function getHiddenSeries(config: PortfolioPlWidgetConfigState) {
    const {
        leavesHideNet: configHidesNet,
        leavesHideSells: configHidesSells,
        leavesHideBuys: configHidesBuys,
        aggregationType,
    } = config;

    const hideNet = configHidesNet || aggregationType !== 'combined';
    const hideBuy = configHidesBuys || aggregationType === 'sells';
    const hideSell = configHidesSells || aggregationType === 'buys';

    return {
        hideNet,
        hideBuy,
        hideSell,
    };
}
